/**
 * ChatScreen
 *
 * CONTENT-ONLY "Chat" screen of the guided product tour. This is the chat half
 * of {@link BunkerProductDemo} lifted into a reusable, shell-agnostic screen:
 * there is NO app window, NO sidebar rail, NO startpage and NO play/pause
 * controls — a parent shell provides the app frame, header and tour controls.
 *
 * The dark electric-blue glass product look is REUSED verbatim from
 * bunker-demo.css (the shared `.bnk-demo` root + its scoped chat classes). A
 * thin `chat-screen.css` adds only the shell-agnostic frame (top strip + flex
 * layout) that the old window/rail structure used to provide.
 *
 * Behaviour: a self-contained sub-sequence driven by an internal state machine
 * that RUNS when `active` is true and RESETS to empty when `active` is false.
 *
 *   chat-typing  → question typewriter into the input
 *   user-sent    → user bubble appears
 *   thinking     → ThinkingProcess card steps through scenario.thinkingSteps
 *   answering    → assistant bubble streams the answer with inline [N] chips
 *   answered     → answer + "Quellen (N)" list fully shown
 *   citation-open→ document viewer panel slides in (highlight sweep + [N])
 *   verified     → green confidence badge → onComplete()
 *
 * - mode='autoplay' (default under no reduced-motion): auto-advances on timers
 *   and auto-opens the citation.
 * - mode='interactive': earlier steps still auto-play up to the answer, then the
 *   [1] citation chip shows a pulsing hotspot the user clicks to open the viewer.
 * - prefers-reduced-motion: renders the final answer + sources immediately (no
 *   typewriter / stream); the citation is still openable.
 *
 * All timers are cleared on unmount and whenever `active` flips to false.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  MessagesSquare,
  Send,
  ShieldCheck,
  Sparkles,
  UserCircle,
  X,
} from 'lucide-react';
import { BunkerMark } from './BunkerMark';
import '../bunker-demo.css';
import './chat-screen.css';
import { wartungScenario, type DemoScenario } from '../bunkerDemoScenario';

/* ─────────────────────────────────────────────────────────────────────────
 * Timings (ms) — lifted from BunkerProductDemo so pacing is identical.
 * ───────────────────────────────────────────────────────────────────────── */

const TYPE_CHAR_MS = 42;
const STREAM_CHAR_MS = 16;
const SEND_DELAY_MS = 450;
const USER_DWELL_MS = 700;
const THINKING_DWELL_MS = 2600;
const ANSWERED_DWELL_MS = 2400;
const CITATION_DWELL_MS = 1400;
const STREAM_TAIL_MS = 250;

/* ─────────────────────────────────────────────────────────────────────────
 * State machine — the chat sub-sequence (no startpage, no loop).
 * ───────────────────────────────────────────────────────────────────────── */

const CHAT_STEPS = [
  'chat-typing', // question typing into the input
  'user-sent', // user bubble appeared
  'thinking', // ThinkingProcess card stepping through the pipeline
  'answering', // assistant bubble streaming the answer
  'answered', // answer + citations fully shown
  'citation-open', // document viewer panel sliding in
  'verified', // self-check confidence badge shown (terminal)
] as const;

type ChatStep = (typeof CHAT_STEPS)[number];

const chatStepIndex = (step: ChatStep): number => CHAT_STEPS.indexOf(step);

const FIRST_STEP: ChatStep = CHAT_STEPS[0];

interface ChatState {
  readonly step: ChatStep;
  /** characters of the question revealed so far (chat-typing). */
  readonly typed: number;
  /** characters of the answer revealed so far (answering). */
  readonly streamed: number;
}

type Action =
  | { type: 'reset' }
  | { type: 'finish' } // jump straight to the verified end-state (reduced motion)
  | { type: 'type-tick'; max: number }
  | { type: 'stream-tick'; max: number }
  | { type: 'send' } // chat-typing → user-sent
  | { type: 'goto'; step: ChatStep }
  | { type: 'stream-done' }; // answering → answered

const initialState: ChatState = { step: FIRST_STEP, typed: 0, streamed: 0 };
const finalState: ChatState = { step: 'verified', typed: 0, streamed: 0 };

function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case 'reset':
      return initialState;
    case 'finish':
      return finalState;
    case 'type-tick':
      return { ...state, typed: Math.min(state.typed + 1, action.max) };
    case 'stream-tick':
      return { ...state, streamed: Math.min(state.streamed + 1, action.max) };
    case 'send':
      return { step: 'user-sent', typed: state.typed, streamed: 0 };
    case 'stream-done':
      return { ...state, step: 'answered' };
    case 'goto':
      return { ...state, step: action.step };
    default:
      return state;
  }
}

/* ─────────────────────────────────────────────────────────────────────────
 * prefers-reduced-motion hook
 * ───────────────────────────────────────────────────────────────────────── */

// Reduced-motion is intentionally NOT honored here: the product tour is
// essential animated showcase content (like the sales deck) and always plays.

/* ─────────────────────────────────────────────────────────────────────────
 * Props
 * ───────────────────────────────────────────────────────────────────────── */

export interface ChatScreenProps {
  /** Drives the sub-sequence: runs when true, resets to empty when false. */
  readonly active: boolean;
  /** Autoplay (timers) or interactive (user clicks the citation hotspot). */
  readonly mode?: 'autoplay' | 'interactive';
  /** Scenario to play. Defaults to the Wartungsintervalle K-300 walkthrough. */
  readonly scenario?: DemoScenario;
  /** Fired once when the sequence reaches the verified end-state. */
  readonly onComplete?: () => void;
  /** Optional className applied to the screen root. */
  readonly className?: string;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Component
 * ───────────────────────────────────────────────────────────────────────── */

export function ChatScreen({
  active,
  mode = 'autoplay',
  scenario = wartungScenario,
  onComplete,
  className,
}: ChatScreenProps) {
  const { t } = useTranslation('demo');
  const [state, dispatch] = useReducer(reducer, initialState);
  const { step, typed, streamed } = state;

  const answerText = useMemo(
    () =>
      scenario.answer
        .map((seg) => (seg.kind === 'text' ? seg.value : `[${seg.ref}]`))
        .join(''),
    [scenario.answer],
  );
  const answerLen = answerText.length;
  const questionLen = scenario.question.length;

  // Keep onComplete fresh without re-arming the terminal effect each render.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  /* ── Reset whenever the screen goes inactive; (re)start when it activates ──
   * Under reduced motion an active screen lands immediately on the final
   * answer + sources end-state. */
  useEffect(() => {
    // Reset on (de)activation; the running effects below drive the sequence
    // from the start whenever the screen is active.
    dispatch({ type: 'reset' });
  }, [active]);

  const running = active;

  /* ── Typewriter: chat-typing fills the input one char at a time ──
   * A single interval (NOT a re-armed-per-tick timeout) owns the whole reveal,
   * so it advances independently of each state commit and tears down cleanly on
   * step change / inactive / unmount. The reducer clamps `typed` at `max`. ── */
  useEffect(() => {
    if (!running || step !== 'chat-typing') return;
    const id = window.setInterval(
      () => dispatch({ type: 'type-tick', max: questionLen }),
      TYPE_CHAR_MS,
    );
    return () => window.clearInterval(id);
  }, [running, step, questionLen]);

  /* ── Once fully typed, auto-"send" after a short beat (both modes). ── */
  useEffect(() => {
    if (!running || step !== 'chat-typing' || typed < questionLen) return;
    const id = window.setTimeout(() => dispatch({ type: 'send' }), SEND_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [running, step, typed, questionLen]);

  /* ── user-sent → thinking ── */
  useEffect(() => {
    if (!running || step !== 'user-sent') return;
    const id = window.setTimeout(() => dispatch({ type: 'goto', step: 'thinking' }), USER_DWELL_MS);
    return () => window.clearTimeout(id);
  }, [running, step]);

  /* ── thinking → answering ── */
  useEffect(() => {
    if (!running || step !== 'thinking') return;
    const id = window.setTimeout(
      () => dispatch({ type: 'goto', step: 'answering' }),
      THINKING_DWELL_MS,
    );
    return () => window.clearTimeout(id);
  }, [running, step]);

  /* ── Answer streaming: answering reveals chars (single interval, same
   * rationale as the typewriter; reducer clamps at `max`). ── */
  useEffect(() => {
    if (!running || step !== 'answering') return;
    const id = window.setInterval(
      () => dispatch({ type: 'stream-tick', max: answerLen }),
      STREAM_CHAR_MS,
    );
    return () => window.clearInterval(id);
  }, [running, step, answerLen]);

  /* ── Once the answer is fully streamed, settle into 'answered'. ── */
  useEffect(() => {
    if (!running || step !== 'answering' || streamed < answerLen) return;
    const id = window.setTimeout(() => dispatch({ type: 'stream-done' }), STREAM_TAIL_MS);
    return () => window.clearTimeout(id);
  }, [running, step, streamed, answerLen]);

  /* ── answered → citation-open.
   * Autoplay auto-opens on a timer; interactive waits for the hotspot click. ── */
  useEffect(() => {
    if (!running || step !== 'answered' || mode !== 'autoplay') return;
    const id = window.setTimeout(
      () => dispatch({ type: 'goto', step: 'citation-open' }),
      ANSWERED_DWELL_MS,
    );
    return () => window.clearTimeout(id);
  }, [running, step, mode]);

  /* ── citation-open → verified ── */
  useEffect(() => {
    if (!running || step !== 'citation-open') return;
    const id = window.setTimeout(
      () => dispatch({ type: 'goto', step: 'verified' }),
      CITATION_DWELL_MS,
    );
    return () => window.clearTimeout(id);
  }, [running, step]);

  /* ── Terminal: fire onComplete once we reach the verified end-state. ── */
  useEffect(() => {
    if (active && step === 'verified') onCompleteRef.current?.();
  }, [active, step]);

  /* Interactive citation click: open the document viewer. */
  const handleCitation = useCallback(() => {
    if (mode !== 'interactive') return;
    if (step !== 'answered') return;
    dispatch({ type: 'goto', step: 'citation-open' });
  }, [mode, step]);

  const handleCloseDoc = useCallback(() => {
    // Closing the viewer advances to the verified end-state.
    dispatch({ type: 'goto', step: 'verified' });
  }, []);

  const messagesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [step, typed, streamed]);

  const stepIdx = chatStepIndex(step);
  const showUser = stepIdx >= chatStepIndex('user-sent');
  const showThinking = stepIdx >= chatStepIndex('thinking');
  const thinkingActive = step === 'thinking';
  const showAnswer = stepIdx >= chatStepIndex('answering');
  const answerDone = stepIdx >= chatStepIndex('answered');
  const docOpen = stepIdx >= chatStepIndex('citation-open');
  const showVerified = stepIdx >= chatStepIndex('verified');

  const inputValue = step === 'chat-typing' ? scenario.question.slice(0, typed) : '';
  const typing = step === 'chat-typing';
  const citationHotspot = mode === 'interactive' && step === 'answered';

  return (
    <div className={`bnk-demo bnk-chat${className ? ` ${className}` : ''}`} data-step={step}>
      {/* slim top strip: title + KI-Profil chip + model badge */}
      <div className="bnk-chat__strip">
        <h3 className="bnk-chat__strip-title">
          <MessagesSquare size={16} strokeWidth={2} color="#66a3ff" />
          {scenario.chatTitle}
        </h3>
        {scenario.comingSoon && (
          <span className="bnk-chat__preview-badge">{t('chat.previewBadge')}</span>
        )}
        <span className="bnk-chat__profile">
          <UserCircle size={13} strokeWidth={2} />
          {t('chat.profile')}
        </span>
        <span className="bnk-chat__model">{t('chat.model')}</span>
      </div>

      <div className="bnk-chat__body">
        <div className="bnk-chat__messages" ref={messagesRef}>
          {showUser && (
            <div className="bnk-demo__row bnk-demo__row--user bnk-demo__enter">
              <div className="bnk-demo__bubble bnk-demo__bubble--user">
                <div className="bnk-demo__bubble-head">
                  {t('chat.userName')}<span className="bnk-demo__bubble-head-time">{t('chat.timeJustNow')}</span>
                </div>
                {scenario.question}
              </div>
              <span className="bnk-demo__avatar bnk-demo__avatar--user">JP</span>
            </div>
          )}

          {showThinking && <ThinkingCard scenario={scenario} active={thinkingActive} />}

          {showAnswer && (
            <div className="bnk-demo__row bnk-demo__enter">
              <span className="bnk-demo__avatar bnk-demo__avatar--ai"><BunkerMark size={20} /></span>
              <div className="bnk-demo__bubble bnk-demo__bubble--ai">
                <div className="bnk-demo__bubble-head bnk-demo__bubble-head--ai">
                  {scenario.wordmark}
                  <span className="bnk-demo__bubble-head-time">{t('chat.timeJustNow')}</span>
                </div>
                <AnswerStream
                  scenario={scenario}
                  streamed={streamed}
                  done={answerDone}
                  citationHotspot={citationHotspot}
                  onCitation={handleCitation}
                />
                {answerDone && <SourcesList scenario={scenario} />}
                {showVerified && (
                  <span className="bnk-demo__verified">
                    <ShieldCheck size={14} strokeWidth={2} />
                    {scenario.verifiedBadge}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* input pinned bottom (textarea look + send button) */}
        <div className="bnk-chat__input-wrap">
          <div className={`bnk-demo__chat-input${typing ? ' bnk-demo__chat-input--focus' : ''}`}>
            <Sparkles size={15} strokeWidth={1.6} color="#4a5568" />
            <span
              className={`bnk-demo__chat-input-text${
                inputValue ? '' : ' bnk-demo__chat-input-text--empty'
              }`}
            >
              {inputValue || scenario.commandPlaceholder}
              {typing && typed < questionLen && <span className="bnk-demo__caret" />}
            </span>
            <button
              type="button"
              disabled
              className="bnk-demo__send"
              aria-label={t('chat.sendQuestion')}
            >
              <Send size={16} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* document viewer panel (slides in over the chat) */}
        {docOpen && (
          <>
            <button
              type="button"
              className="bnk-demo__doc-scrim"
              onClick={handleCloseDoc}
              aria-label={t('chat.closeDocView')}
            />
            <DocPanel scenario={scenario} onClose={handleCloseDoc} />
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * ThinkingProcess card (spinner → checks), lifted from BunkerProductDemo.
 * ───────────────────────────────────────────────────────────────────────── */

function ThinkingCard({
  scenario,
  active,
}: {
  readonly scenario: DemoScenario;
  readonly active: boolean;
}) {
  const { t } = useTranslation('demo');
  return (
    <div className="bnk-demo__row bnk-demo__enter">
      <span className="bnk-demo__avatar bnk-demo__avatar--ai"><BunkerMark size={20} /></span>
      <div className="bnk-demo__thinking" style={{ flex: 1 }}>
        <div className="bnk-demo__thinking-head">
          {active ? (
            <span className="bnk-demo__spinner" />
          ) : (
            <span className="bnk-demo__tstep-mark bnk-demo__tstep-mark--done">✓</span>
          )}
          {active ? t('chat.thinkingActive') : t('chat.thinkingDone')}
        </div>
        <div className="bnk-demo__thinking-steps">
          {scenario.thinkingSteps.map((s, i) => {
            const isLast = i === scenario.thinkingSteps.length - 1;
            const done = !active || !isLast;
            return (
              <div key={s.label} className="bnk-demo__tstep">
                {done ? (
                  <span className="bnk-demo__tstep-mark bnk-demo__tstep-mark--done">✓</span>
                ) : (
                  <span className="bnk-demo__tstep-mark bnk-demo__tstep-mark--active" />
                )}
                <div>
                  <div className="bnk-demo__tstep-label">{s.label}</div>
                  {s.meta && <div className="bnk-demo__tstep-meta">{s.meta}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Streamed answer with inline [N] citation chips, lifted from BunkerProductDemo.
 * ───────────────────────────────────────────────────────────────────────── */

function AnswerStream({
  scenario,
  streamed,
  done,
  citationHotspot,
  onCitation,
}: {
  readonly scenario: DemoScenario;
  readonly streamed: number;
  readonly done: boolean;
  readonly citationHotspot: boolean;
  readonly onCitation: () => void;
}) {
  const { t } = useTranslation('demo');
  // Walk the segments, revealing only `streamed` characters total. Citation
  // tokens count as their rendered length "[N]".
  let remaining = done ? Number.POSITIVE_INFINITY : streamed;
  const out: ReactElement[] = [];

  scenario.answer.forEach((seg, idx) => {
    if (remaining <= 0) return;
    if (seg.kind === 'text') {
      const slice = seg.value.slice(0, remaining);
      remaining -= seg.value.length;
      if (slice) out.push(<span key={`t${idx}`}>{slice}</span>);
    } else {
      const token = `[${seg.ref}]`;
      if (remaining < token.length) {
        remaining = 0;
        return;
      }
      remaining -= token.length;
      // The first citation chip carries the pulsing hotspot once the answer is
      // fully streamed (interactive mode).
      const isPrimary = seg.ref === 1;
      const clickable = done && isPrimary;
      out.push(
        <button
          key={`c${idx}`}
          type="button"
          onClick={clickable ? onCitation : undefined}
          className={[
            'bnk-demo__chip',
            clickable ? 'bnk-demo__chip--clickable' : '',
            clickable && citationHotspot ? 'bnk-demo__hotspot' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={t('chat.openSource', { ref: seg.ref })}
          tabIndex={clickable ? 0 : -1}
        >
          [{seg.ref}]
        </button>,
      );
    }
  });

  return (
    <div>
      {out}
      {!done && <span className="bnk-demo__caret" />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Sources list ("Quellen (N)"), lifted from BunkerProductDemo.
 * ───────────────────────────────────────────────────────────────────────── */

function SourcesList({ scenario }: { readonly scenario: DemoScenario }) {
  const { t } = useTranslation('demo');
  return (
    <div className="bnk-demo__sources bnk-demo__enter">
      <div className="bnk-demo__sources-head">
        <FileText size={13} strokeWidth={2} />
        {t('chat.sources')} <span className="bnk-demo__count">({scenario.citations.length})</span>
      </div>
      {scenario.citations.map((c) => (
        <div key={c.n} className="bnk-demo__source">
          <span className="bnk-demo__source-num">{c.n}</span>
          <span className="bnk-demo__source-name">{c.document}</span>
          <span className="bnk-demo__source-page">{c.page}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Document viewer panel (highlight sweep + [N] marker), lifted from BunkerProductDemo.
 * ───────────────────────────────────────────────────────────────────────── */

function DocPanel({
  scenario,
  onClose,
}: {
  readonly scenario: DemoScenario;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('demo');
  const { viewer } = scenario;
  return (
    <aside className="bnk-demo__doc-panel" aria-label={t('chat.docView')}>
      <div className="bnk-demo__doc-head">
        <FileText size={15} strokeWidth={2} color="#66a3ff" />
        <span className="bnk-demo__doc-head-title">{viewer.title}</span>
        <span className="bnk-demo__doc-head-page">{viewer.page}</span>
        <button
          type="button"
          className="bnk-demo__doc-close"
          onClick={onClose}
          aria-label={t('chat.close')}
        >
          <X size={14} strokeWidth={2.2} />
        </button>
      </div>
      <div className="bnk-demo__doc-body">
        <div className="bnk-demo__pdf">
          {viewer.lines.map((line, i) => {
            const isHi = i === viewer.highlightLine;
            return (
              <div
                key={i}
                className={`bnk-demo__pdf-line${isHi ? ' bnk-demo__pdf-line--highlight' : ''}`}
              >
                {isHi && (
                  <span className="bnk-demo__pdf-marker" aria-hidden="true">
                    {viewer.marker}
                  </span>
                )}
                {line || ' '}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

export default ChatScreen;
